import { pushURLQuery } from 'router/UrlQueries';
import * as urlQueryFields from 'router/urlQueryFields';
import * as actionTypes from 'store/actions/actionTypes';
import * as actions from 'store/actions/index';
import AgStack from 'utils/AgStackAPI';
import { toast } from "react-toastify";
import { createFeatureCollectionFromJSON } from '../../utils/geojsonUtils';
import { getBoundaryInfo } from 'utils/boundaries';
import { getSharedBoundary } from 'utils/shareBoundary';

const googleLoginModalShow = (enable) => ({
	type: actionTypes.SHOW_GOOGLE_LOGIN_MODAL,
	enable,
});

const modalDisclaimerShow = (enable) => ({
	type: actionTypes.SHOW_MODAL_DISCLAIMER,
	enable,
});

const drawerMenuToggle = (open) => ({
	type: actionTypes.DRAWER_MENU_TOGGLE,
	open,
});

export const showGoogleLoginModal = (enable) => (dispatch) => {
	dispatch(googleLoginModalShow(enable));
};

export const showModalDisclaimer = (enable) => (dispatch) => {
	dispatch(modalDisclaimerShow(enable));
};

export const toggleDrawerMenu = (open) => (dispatch) => {
	pushURLQuery(urlQueryFields.mainMenu, open);
	dispatch(drawerMenuToggle(open));
};


export const addGeoIds = function(geoIdsReceived, dispatch, intl = null) {
	try {
		const agStack = new AgStack();
		const geoIds = geoIdsReceived.split(',');
		let plotIdx = 1;

		geoIds.forEach(geoId => {
			geoId = geoId.trim();
			if (geoId === "") return;
			agStack.getGeoJSON(geoId).then((response) => {
				const name = `Plot ${plotIdx} : ${geoId}`;
				const geoJson = { ...response, properties: { ...response.properties, geoId, name } };

				dispatch(actions.addTempBoundary(name, geoJson));
				if (plotIdx === 1) {
					dispatch(actions.addTempPolygon(name, geoJson));
					dispatch(actions.selectBoundaryId(name));
				}
				plotIdx++;

			}).catch((error) => {
				console.error(error);
				toast.error(
					intl ? intl.formatMessage({ id: 'dashboard_geoId_not_found', defaultMessage: 'Geo ID {geoId} not found. Please check for typos!' }, { geoId }) : `Geo ID ${geoId} not found. Please check for typos!`,
					{ autoClose: 10000 }
				);
			});
		})
	} catch (error) {
		console.error(error);
	}

}

export function fetchJson(url, dispatch, intl = null) {
	// Remove double quotes from the URL
	url = url.replace(/"/g, '');

	// Use a relative URL for the API call
	let functionUrl = `/api/proxyFetch?url=${encodeURIComponent(url)}`;

	// Are we running in localhost?
	if (window.location.hostname === 'localhost') {
		// Specify the port 5000. In dev and prod the port is not specified
		functionUrl = 'http://localhost:5000' + functionUrl;
	}

	// Fetch the JSON data from the constructed URL
	fetch(functionUrl)
		.then(response => {
			if (!response.ok) {
				throw new Error('Network response was not ok ' + response.statusText);
			}
			response.json().then(jsonData => {
				const transformedGeojson = createFeatureCollectionFromJSON(jsonData);
				const nameCounts = {};

				if (transformedGeojson.name) {
					dispatch(actions.setTempBoundariesName(transformedGeojson.name));
				}

				transformedGeojson.features.forEach((feature, index) => {
					let originalName = getBoundaryInfo(feature).name || `Feature ${index + 1}`;
					let name = originalName;

					// Ensure unique names by adding suffix
					if (nameCounts[name]) {
						nameCounts[name]++;
						name = `${originalName} (${nameCounts[name]})`;
					} else {
						nameCounts[name] = 1;
					}

					dispatch(actions.addTempBoundary(name, feature));

					if (index === 0) {
						dispatch(actions.addTempPolygon(name, feature));
						dispatch(actions.selectBoundaryId(name));
					}
				});
			}).catch(error => {
				console.warn('There has been a problem with your JSON parsing:', error);
				toast.warn(
					intl ? intl.formatMessage({ id: 'dashboard_invalid_geojson', defaultMessage: 'The URL in fetchJson does not contain a valid GeoJSON!' }) : 'The URL in fetchJson does not contain a valid GeoJSON!',
					{ autoClose: 10000 }
				);
			})
		})
		.catch(error => {
			console.warn('There has been a problem with your fetch operation:', error);
			toast.warn(
				intl ? intl.formatMessage({ id: 'dashboard_url_not_accessible', defaultMessage: 'The URL in fetchJson is not accessible or not allowed!' }) : 'The URL in fetchJson is not accessible or not allowed!',
				{ autoClose: 10000 }
			);
		});
}

export function handlePolygonFromUrl(geoJson, unnamedArea, dispatch) {
	const transformedGeojson = createFeatureCollectionFromJSON(geoJson);

	if (transformedGeojson.name) {
		dispatch(actions.setTempBoundariesName(transformedGeojson.name));
	}
	transformedGeojson.features.forEach(feature => {

		var name = getBoundaryInfo(feature).name || unnamedArea;

		dispatch(actions.addTempBoundary(name, feature));
		// Only for the first feature, select it
		if (transformedGeojson.features.indexOf(feature) === 0) {
			dispatch(actions.addTempPolygon(name, feature));
			dispatch(actions.selectBoundaryId(name));
		}
	});
}

/**
 * Handles loading a shared boundary using the share ID from URL
 * @param {string} shareId - The ID of the shared boundary
 * @param {Function} dispatch - Redux dispatch function
 */
export function handleSharedBoundary(shareId, dispatch, intl = null) {
  if (!shareId) return;
  
  toast.info(
    intl ? intl.formatMessage({ id: 'dashboard_loading_shared_boundary', defaultMessage: 'Loading shared boundary...' }) : 'Loading shared boundary...',
    { autoClose: 3000 }
  );
  
  // Create an abort controller for cancellation
  const abortController = new AbortController();
  
  // Request the shared boundary data
  getSharedBoundary(shareId)
    .then((boundaryData) => {
      if (boundaryData && boundaryData.geoJson) {
        // Get a name for the boundary from metadata or default to a timestamp
        const boundaryName = boundaryData.metadata?.name || 
          `Shared Boundary (${new Date(boundaryData.timestamp).toLocaleDateString()})`;
        
        // Create feature collection if needed
        const transformedGeojson = createFeatureCollectionFromJSON(boundaryData.geoJson);
        
        // Set temp boundaries name if available in metadata
        if (boundaryData.metadata?.name) {
          dispatch(actions.setTempBoundariesName(boundaryData.metadata.name));
        }
        
        // Add each feature in the collection
        transformedGeojson.features.forEach(feature => {
          // Get name from properties or use default
          const name = getBoundaryInfo(feature).name || boundaryName;
          
          dispatch(actions.addTempBoundary(name, feature));
          
          // Only select the first feature
          if (transformedGeojson.features.indexOf(feature) === 0) {
            dispatch(actions.addTempPolygon(name, feature));
            dispatch(actions.selectBoundaryId(name));
          }
        });
        
        toast.success(
          intl ? intl.formatMessage({ id: 'dashboard_shared_boundary_loaded', defaultMessage: 'Shared boundary loaded successfully' }) : 'Shared boundary loaded successfully',
          { autoClose: 3000 }
        );
      }
    })
    .catch((error) => {
      console.error('Error loading shared boundary:', error);
      toast.error(
        intl ? intl.formatMessage({ id: 'dashboard_shared_boundary_error', defaultMessage: 'Error loading shared boundary. The link may be invalid or expired.' }) : 'Error loading shared boundary. The link may be invalid or expired.',
        { autoClose: 5000 }
      );
    });
}

export const loadStateFromUrl = (urlQueries, unnamedArea, intl = null) => async (dispatch) => {
    const queryKeys = Object.keys(urlQueries);

    // Extract parameters for ordered processing
    const aoiToLoad = urlQueries[urlQueryFields.aoi];
    const boundaryToSelect = urlQueries[urlQueryFields.boundary];
    const polygonToLoad = urlQueries[urlQueryFields.polygon];
    const fetchJsonToLoad = urlQueries[urlQueryFields.fetchJson];
    const shareIdToLoad = urlQueries[urlQueryFields.shareId];
    const geoIdsToLoad = urlQueries[urlQueryFields.geoId] || urlQueries[urlQueryFields.geoIds];
    const layersToToggle = urlQueries[urlQueryFields.layers] ? JSON.parse(urlQueries[urlQueryFields.layers]) : null;
    const scriptsToLoad = urlQueries[urlQueryFields.scripts] ? JSON.parse(urlQueries[urlQueryFields.scripts]) : null;

    // ============================================================================
    // PHASE 1: Load AOI first (critical - provides layers and context)
    // ============================================================================
    if (aoiToLoad) {
        try {
            await dispatch(actions.selectArea(aoiToLoad));
            // Wait for AOI layers to be loaded and available in Redux
            await new Promise(resolve => setTimeout(resolve, 2000));
        } catch (error) {
            console.error('[loadStateFromUrl] Error loading AOI:', error);
            toast.error(
                intl ? intl.formatMessage({ id: 'dashboard_error_loading_aoi', defaultMessage: 'Error loading area: {message}' }, { message: error.message }) : `Error loading area: ${error.message}`,
                { autoClose: 10000 }
            );
        }
    }

    // Set statistics drawer state immediately after AOI loads (if specified in URL)
    // This prevents the default opening behavior from selectArea
    if (urlQueryFields.statisticsOpen in urlQueries) {
        try {
            const statisticsOpenValue = urlQueries[urlQueryFields.statisticsOpen] === 'true';
            dispatch(actions.openStatisticsDrawer(statisticsOpenValue));
        } catch (error) {
            console.error('[loadStateFromUrl] Error setting statistics drawer:', error);
        }
    }

    // ============================================================================
    // PHASE 2: Load boundary (from various sources)
    // ============================================================================

    // Handle polygon parameter (inline GeoJSON)
    if (polygonToLoad) {
        try {
            const geoJson = JSON.parse(polygonToLoad);
            handlePolygonFromUrl(geoJson, unnamedArea, dispatch);
            // Wait for polygon to be processed
            await new Promise(resolve => setTimeout(resolve, 500));
        } catch (error) {
            console.error('[loadStateFromUrl] Error parsing polygon GeoJSON:', error);
            toast.error(
                intl ? intl.formatMessage({ id: 'dashboard_error_parsing_polygon', defaultMessage: 'Error parsing polygon: {message}' }, { message: error.message }) : `Error parsing polygon: ${error.message}`,
                { autoClose: 10000 }
            );
        }
    }

    // Handle fetchJson parameter (external GeoJSON URL)
    if (fetchJsonToLoad) {
        try {
           fetchJson(fetchJsonToLoad, dispatch, intl);
            // Wait for fetch to complete (it's async)
            await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (error) {
            console.error('[loadStateFromUrl] Error fetching JSON:', error);
            toast.error(
                intl ? intl.formatMessage({ id: 'dashboard_error_fetching_json', defaultMessage: 'Error fetching GeoJSON: {message}' }, { message: error.message }) : `Error fetching GeoJSON: ${error.message}`,
                { autoClose: 10000 }
            );
        }
    }

    // Handle shareId parameter (shared boundary)
    if (shareIdToLoad) {
        try {
           handleSharedBoundary(shareIdToLoad, dispatch, intl);
            // Wait for shared boundary to load
            await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (error) {
            console.error('[loadStateFromUrl] Error loading shared boundary:', error);
            toast.error(
                intl ? intl.formatMessage({ id: 'dashboard_error_loading_shared', defaultMessage: 'Error loading shared boundary: {message}' }, { message: error.message }) : `Error loading shared boundary: ${error.message}`,
                { autoClose: 10000 }
            );
        }
    }

    // Handle geoId/geoIds parameter (AgStack geometries)
    if (geoIdsToLoad) {
        try {
             addGeoIds(geoIdsToLoad, dispatch, intl);
            // Wait for geoIds to load
            await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (error) {
            console.error('[loadStateFromUrl] Error processing geoIds:', error);
            toast.error(
                intl ? intl.formatMessage({ id: 'dashboard_error_loading_geoids', defaultMessage: 'Error loading geoIds: {message}' }, { message: error.message }) : `Error loading geoIds: ${error.message}`,
                { autoClose: 10000 }
            );
        }
    }

    // Handle explicit boundary selection (from AOI boundaries)
    if (boundaryToSelect && !polygonToLoad && !fetchJsonToLoad && !shareIdToLoad && !geoIdsToLoad) {
        try {
            dispatch(actions.selectBoundaryId(boundaryToSelect));
            // Wait for boundary to be selected
            await new Promise(resolve => setTimeout(resolve, 500));
        } catch (error) {
            console.error('[loadStateFromUrl] Error selecting boundary:', error);
            toast.error(
                intl ? intl.formatMessage({ id: 'dashboard_error_selecting_boundary', defaultMessage: 'Error selecting boundary: {message}' }, { message: error.message }) : `Error selecting boundary: ${error.message}`,
                { autoClose: 10000 }
            );
        }
    }

    // ============================================================================
    // PHASE 3: Wait for boundary to be loaded in map
    // Note: Feature selection happens automatically in Map.js addBoundary function
    // ============================================================================
    await new Promise(resolve => setTimeout(resolve, 1500));

    // ============================================================================
    // PHASE 4: Load layers and scripts (after boundary is ready)
    // ============================================================================
 
    // Load layers
    // NOTE: Layers from URL are handled by Map.js component on initialization
    // We should NOT toggle them here to avoid double-toggling (which would turn them off)
    // Map.js reads the 'layers' URL parameter directly and loads them

    // Load scripts
    if (scriptsToLoad) {
        try {
            const scripts = [];

            Object.keys(scriptsToLoad).forEach((scriptId) => {
                const { dateRange, availableYears } = scriptsToLoad[scriptId];

                const scriptConfig = {
                    id: scriptId,
                    timePeriod: scriptsToLoad[scriptId].timePeriod,
                    dateRange: {
                        startYear: dateRange?.[0] ?? (availableYears?.[0] ?? null),
                        endYear: dateRange?.[1] ?? (availableYears?.[1] ?? null),
                    },
                };

                scripts.push(scriptConfig);
            });

            dispatch(actions.loadPreselectedScripts(scripts));
        } catch (error) {
            console.error('[loadStateFromUrl] Error processing scripts:', error);
            toast.error(
                intl ? intl.formatMessage({ id: 'dashboard_error_processing_scripts', defaultMessage: 'Error processing scripts: {message}' }, { message: error.message }) : `Error processing scripts: ${error.message}`,
                { autoClose: 10000 }
            );
        }
    }

    // ============================================================================
    // PHASE 5: Load other parameters (UI state, language, etc.)
    // ============================================================================

    if (urlQueryFields.mainMenu in urlQueries) {
        try {
            const mainMenuValue = urlQueries[urlQueryFields.mainMenu] === 'true';
            console.log('[loadStateFromUrl] Setting main menu state:', mainMenuValue);
            dispatch(actions.toggleDrawerMenu(mainMenuValue));
        } catch (error) {
            console.error('[loadStateFromUrl] Error toggling main menu:', error);
        }
    }

    // Note: statisticsOpen is handled in Phase 1 (right after AOI loads)
    // to prevent conflicts with selectArea's default opening behavior

    if (urlQueryFields.lang in urlQueries) {
        try {
             dispatch(actions.setSelectedLanguage(urlQueries[urlQueryFields.lang]));
        } catch (error) {
            console.error('[loadStateFromUrl] Error setting language:', error);
        }
    }

};

